import React, { useState, useEffect } from 'react';
import { CustomButton } from '../../../common/ReusableComponent/CustomButton';
import useApi from "../../../../utils/api-manager/Helper/useApi";
import SuccessModal from '../Reusable/SuccessModal';
import './OfferLine.css';

const OfferLine = ({ storeId }) => {
    const { Get, Post, Patch, getAPI } = useApi();
    const [loading, setLoading] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [offerLineId, setOfferLineId] = useState(null);

    const [formData, setFormData] = useState({
        text: '',
        is_active: true
    });

    const gradientOptions = [
        { value: 'from-[#91DDCF] to-[#088F8F]', label: 'Teal Gradient', preview: 'linear-gradient(to right, #91DDCF, #088F8F)' },
        { value: 'from-[#FF6B6B] to-[#FF8E53]', label: 'Orange Gradient', preview: 'linear-gradient(to right, #FF6B6B, #FF8E53)' },
        { value: 'from-[#4ECDC4] to-[#44A08D]', label: 'Green Gradient', preview: 'linear-gradient(to right, #4ECDC4, #44A08D)' },
        { value: 'from-[#667eea] to-[#764ba2]', label: 'Purple Gradient', preview: 'linear-gradient(to right, #667eea, #764ba2)' },
        { value: 'from-[#f093fb] to-[#f5576c]', label: 'Pink Gradient', preview: 'linear-gradient(to right, #f093fb, #f5576c)' },
        { value: 'from-[#4facfe] to-[#00f2fe]', label: 'Blue Gradient', preview: 'linear-gradient(to right, #4facfe, #00f2fe)' },
        { value: 'from-[#43e97b] to-[#38f9d7]', label: 'Mint Gradient', preview: 'linear-gradient(to right, #43e97b, #38f9d7)' },
        { value: 'from-[#fa709a] to-[#fee140]', label: 'Sunset Gradient', preview: 'linear-gradient(to right, #fa709a, #fee140)' }
    ];

    const speedOptions = [
        { value: '10s', label: 'Fast (10s)' },
        { value: '15s', label: 'Medium (15s)' },
        { value: '20s', label: 'Slow (20s)' },
        { value: '30s', label: 'Very Slow (30s)' }
    ];

    useEffect(() => {
        if (storeId) {
            fetchOfferLine();
        }
    }, [storeId]);

    const fetchOfferLine = async () => {
        if (!storeId) return;

        setLoading(true);
        try {
            const domain = window.location.hostname;
            const apiUrl = `${getAPI('ecomOfferLine')}?store_id=${storeId}&domain=${domain}`;
            console.log('[OfferLine] Fetching from:', apiUrl);
            const result = await Get(apiUrl);
            console.log('[OfferLine] API Response:', result);

            // Handle paginated response
            const dataArray = result?.results || result;

            if (dataArray && Array.isArray(dataArray) && dataArray.length > 0) {
                const data = dataArray.find(item => item.store === storeId) || dataArray[0];
                console.log('[OfferLine] Found data:', data);
                setOfferLineId(data.id);
                setFormData({
                    text: data.text || '',
                    is_active: data.is_active !== undefined ? data.is_active : true
                });
            } else {
                console.log('[OfferLine] No data found, resetting form');
                setOfferLineId(null);
                setFormData({
                    text: '',
                    is_active: true
                });
            }
        } catch (error) {
            console.error('[OfferLine] Error fetching offer line:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!storeId) {
            setSuccessMessage('Store ID not found');
            setShowSuccessModal(true);
            return;
        }

        setLoading(true);
        try {
            const domain = window.location.hostname;
            const payload = {
                text: formData.text,
                is_active: formData.is_active,
                store: storeId
            };

            let result;
            if (offerLineId) {
                console.log('[OfferLine] Updating offer line with id:', offerLineId);
                result = await Patch('ecomOfferLine', `${offerLineId}?domain=${domain}`, payload);
            } else {
                console.log('[OfferLine] Creating new offer line');
                const apiUrl = `${getAPI('ecomOfferLine')}?store_id=${storeId}&domain=${domain}`;
                result = await Post(apiUrl, payload);
            }

            if (result) {
                if (!offerLineId && result.data?.id) {
                    setOfferLineId(result.data.id);
                }
                setSuccessMessage('Offer line saved successfully!');
                setShowSuccessModal(true);
                await fetchOfferLine();
            }
        } catch (error) {
            console.error('[OfferLine] Error saving offer line:', error);
            setSuccessMessage('Failed to save offer line');
            setShowSuccessModal(true);
        } finally {
            setLoading(false);
        }
    };

    const OfferPreview = () => (
        <div className="offer-preview">
            <div className="offer-line-preview" style={{ background: 'linear-gradient(to right, #91DDCF, #088F8F)', padding: '12px', borderRadius: '8px', color: 'white', textAlign: 'center', fontWeight: '600' }}>
                {formData.text || 'Enter offer text to preview...'}
            </div>
        </div>
    );

    return (
        <>
            <SuccessModal
                show={showSuccessModal}
                message={successMessage}
                onClose={() => setShowSuccessModal(false)}
            />

            <div className="content-card">
                <div className="content-card-header">
                    <div className="card-title">
                        <h3>Offer Text</h3>
                    </div>
                </div>
                <div className="edit-section">
                    <textarea
                        value={formData.text}
                        onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                        className="text-input"
                        rows={3}
                        placeholder="e.g., 🔥 Flat 50% OFF • Free Delivery • Order Now"
                    />
                </div>
            </div>

            <div className="content-card">
                <div className="content-card-header">
                    <div className="card-title">
                        <h3>Status</h3>
                    </div>
                </div>
                <div className="edit-section">
                    <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#374151' }}>
                        <input
                            type="checkbox"
                            checked={formData.is_active}
                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                            style={{ margin: 0 }}
                        />
                        <span>Active</span>
                    </label>
                </div>
            </div>

            <div className="content-card">
                <div className="content-card-header">
                    <div className="card-title">
                        <h3>Live Preview</h3>
                    </div>
                </div>
                <div className="edit-section">
                    <OfferPreview />
                </div>
            </div>

            <div className="content-card">
                <div className="edit-section">
                    <CustomButton
                        text={loading ? 'Saving...' : 'Save Offer Line'}
                        backgroundColor="rgb(74, 85, 70)"
                        color="#fff"
                        width="200px"
                        height="45px"
                        onClick={handleSave}
                        disabled={loading}
                    />
                </div>
            </div>
        </>
    );
};

export default OfferLine;